import decamelizeKeys from 'decamelize-keys'
import { saveAs } from 'file-saver'
import queryString from 'query-string'

import analytics from '@/modules/analytics'
import { fileFormats } from '@/modules/api/types'

type Cancellable<T> = Promise<T> & { cancel(): void }

export const toUrlSearchParamsString = (searchParams: Record<string, any>): string =>
  queryString.stringify(decamelizeKeys(searchParams), {
    skipEmptyString: true,
  })

export const getNextPageParam = <T>(lastPage: PopulusPage<T>) =>
  !lastPage.page || lastPage.page === lastPage.pages ? undefined : lastPage.page + 1

export const getPreviousPageParam = <T>(firstPage: PopulusPage<T>) =>
  !firstPage.page || firstPage.page === 1 ? undefined : firstPage.page - 1

export type PopulusPage<T> = {
  items: Array<T>
  total?: number | null
  page: number | null
  size: number | null
  pages?: number | null
}

export const emptyPaginatedResponse = {
  items: [],
  total: 0,
  page: 1,
  size: 0,
}

export const cancellableQueryFn =
  <T>(fn: () => Cancellable<T>) =>
  ({ signal }: { signal: AbortSignal }): Promise<T> => {
    const promise = fn()
    signal.addEventListener('abort', () => promise.cancel())
    return promise
  }

export const saveFile = async (
  data: any,
  fileName: string,
  outputFormat: keyof typeof fileFormats
) => {
  analytics.track(`Download ${fileName}`, {
    category: 'Download',
  })

  let blob: Blob
  if (outputFormat === 'json') {
    blob = new Blob([JSON.stringify(data)], { type: 'application/json' })
  } else if (outputFormat === 'zip' || fileName.endsWith('.zip')) {
    blob = new Blob([data], { type: 'application/zip' })
  } else {
    blob = new Blob([data])
  }

  saveAs(blob, fileName)
}
